import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { Page } from '@/components/Page'
import { formatPKR } from '@/lib/money'
import { openWorkspaceDb } from '@/offline/db'
import { getActiveWorkspaceId } from '@/features/workspace/workspace'
import { buildContact } from './contactPayload'
import { saveContact } from './saveContact'
import { contactBalances } from '@/features/transactions/transactions'

const inputCls =
  'w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:outline-none'
const labelCls = 'mb-1 block text-xs font-medium text-slate-400'

/** Contacts = accounts-receivable subledger: per-party outstanding + aging buckets. */
export function ContactsPage() {
  const ws = getActiveWorkspaceId()

  const contacts = useLiveQuery(async () => (ws ? openWorkspaceDb(ws).contacts.where('workspace_id').equals(ws).toArray() : []), [ws])
  const sales = useLiveQuery(async () => (ws ? openWorkspaceDb(ws).sales.where('workspace_id').equals(ws).toArray() : []), [ws])
  const payments = useLiveQuery(async () => (ws ? openWorkspaceDb(ws).payments.where('workspace_id').equals(ws).toArray() : []), [ws])

  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [kind, setKind] = useState<'customer' | 'seller' | 'both'>('customer')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const balances = contactBalances(sales ?? [], payments ?? [], contacts ?? [])
  const totalOwed = balances.reduce((s, b) => s + b.outstanding, 0)

  async function onAdd(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!ws) return
    if (!name.trim()) return setError('Name is required.')
    setBusy(true)
    try {
      const summary = await saveContact(ws, buildContact({ workspace_id: ws, name, phone, kind }))
      if (summary.rejected.length > 0) {
        setError(`Not saved: ${summary.rejected[0].result.status}`)
        return
      }
      setName('')
      setPhone('')
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Page title="Contacts" note={`Total owed by customers: ${formatPKR(totalOwed)}`}>
      <form onSubmit={onAdd} className="mb-4 space-y-2 rounded-xl border border-slate-800 bg-slate-900/40 p-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={labelCls} htmlFor="c-name">Name *</label>
            <input id="c-name" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="Customer / seller" />
          </div>
          <div>
            <label className={labelCls} htmlFor="c-phone">Phone</label>
            <input id="c-phone" className={inputCls} inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0300…" />
          </div>
        </div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className={labelCls} htmlFor="c-kind">Type</label>
            <select id="c-kind" className={inputCls} value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
              <option value="customer">Customer</option>
              <option value="seller">Seller</option>
              <option value="both">Both</option>
            </select>
          </div>
          <button type="submit" disabled={busy} className="rounded-lg bg-sky-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
            {busy ? '…' : 'Add'}
          </button>
        </div>
        {error ? <p role="alert" className="text-xs text-rose-300">{error}</p> : null}
      </form>

      {balances.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-700 px-4 py-8 text-center text-sm text-slate-500">
          No contacts yet. Add customers/sellers to track per-party balances.
        </p>
      ) : (
        <ul className="space-y-2">
          {balances.map((b) => (
            <li key={b.contact.id} className="rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2.5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-100">{b.contact.name}</p>
                  <p className="text-[11px] text-slate-500">
                    {b.contact.kind}
                    {b.contact.phone ? ` • ${b.contact.phone}` : ''}
                  </p>
                </div>
                <span className={`text-sm font-semibold tabular-nums ${b.outstanding > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                  {formatPKR(b.outstanding)}
                </span>
              </div>
              {b.outstanding > 0 ? (
                <div className="mt-1 flex flex-wrap gap-x-3 text-[11px] text-slate-400">
                  <span>0-30d: {formatPKR(b.aging.d0_30, { symbol: false })}</span>
                  <span className={b.aging.d31_60 > 0 ? 'text-amber-400' : ''}>31-60d: {formatPKR(b.aging.d31_60, { symbol: false })}</span>
                  <span className={b.aging.d60p > 0 ? 'text-rose-400' : ''}>60d+: {formatPKR(b.aging.d60p, { symbol: false })}</span>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <p className="mt-3 text-center">
        <Link to="/receivables" className="text-xs font-semibold text-amber-400 underline underline-offset-2">
          View all outstanding (incl. walk-in) →
        </Link>
      </p>
    </Page>
  )
}
